import React, { useId, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Empty, Modal, Space, Table, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { GET_ROLES } from "graphql/query/role-gql";
import { useTranslation } from "components/use-hooks";
import EventConst from "components/events/event-const";
import SubcontractorTitleLinkField from "components/subcontractor/fields/subcontractor-title-link-field";
import useBookedSubcontractors from "./hooks/use-booked-subcontractors";
import { getBookingDifference, isBookedWorkersValid } from "./helpers/booking-policy";
import BookingForm from "./forms/booking-form";
import BookingActionField from "./fields/booking-action-field";
import "./booked-subcontractors.scss";


const errorText = (error, text) => {
    const keys = {
        FORBIDDEN: "NoEditPermission",
        INVALID_QUANTITY: "InvalidQuantity",
        DUPLICATE_COMPANY: "DuplicateCompany",
        REQUEST_PENDING: "RequestPending",
        NEEDS_REFRESH: "NeedsRefresh",
        STALE_BOOKING: "NeedsRefresh",
        COMPANY_REQUIRED: "ChooseCompany",
        UNCONFIRMED_MUTATION: "UnconfirmedChange",
        UNCONFIRMED_REFRESH: "UnconfirmedChange",
        INVALID_RESPONSE: "UnconfirmedChange",
        DELETE_UNCONFIRMED: "DeleteUnconfirmed",
    };
    if (keys[error?.code]) return text(keys[error.code]);
    return (!error?.code || error.code === "API_ERROR") && error?.message && error.message !== "API_ERROR"
        ? error.message : text("SaveFailed");
};

const BookingContent = ({ eventID, state, text, viewLabel, registeredScopeHint }) => {
    const { rows, loading, error, refresh, canEdit, saving, saveBooking, deleteBooking, refreshWarning } = state;
    const [ dialog, setDialog ] = useState(null);
    const [ actionError, setActionError ] = useState(null);
    const modalTitleID = useId();
    const writeDisabled = loading || Boolean(error) || Boolean(refreshWarning) || saving || !canEdit;
    const open = (kind, booking) => {
        setActionError(null);
        setDialog({ kind, booking });
    };
    const close = () => {
        if (!saving) {
            setDialog(null);
            setActionError(null);
        }
    };
    const reload = () => {
        setActionError(null);
        refresh().catch(() => {});
    };
    const retryButton = <Button className="ant-btn-secondary bg-light-gray" loading={ loading }
        disabled={ loading || saving } onClick={ reload }>{ text("Retry") }</Button>;
    const needsReload = ["NEEDS_REFRESH", "STALE_BOOKING", "UNCONFIRMED_MUTATION", "UNCONFIRMED_REFRESH",
        "INVALID_RESPONSE", "DELETE_UNCONFIRMED"].includes(actionError?.code);
    const submit = async values => {
        setActionError(null);
        try {
            const committed = dialog.kind === "delete"
                ? await deleteBooking(dialog.booking)
                : await saveBooking(values, dialog.booking);
            if (committed) {
                message.success(text(dialog.kind === "delete" ? "Deleted" : "Saved"));
                setDialog(null);
            }
        } catch (failure) {
            if (failure?.code === "DELETE_UNCONFIRMED") setDialog(null);
            if (failure?.code !== "STALE_CONTEXT") setActionError(failure);
        }
    };
    const renderCount = value => isBookedWorkersValid(value)
        ? <span className="booked-subcontractors__count">{ value }</span> : text("Unavailable");
    const columns = [
        { title: text("Company"), key: "company", render: (_, row) => row.relSubcontractor?.title
            ? <SubcontractorTitleLinkField subcontractor={ row.relSubcontractor } /> : text("Unavailable") },
        { title: text("Booked"), dataIndex: "booked_workers", key: "booked", align: "center", render: renderCount },
        { title: text("Registered"), dataIndex: "getRegisteredWorkers", key: "registered", align: "center", render: (registered, row) => {
            const difference = getBookingDifference(row.booked_workers, row.getRegisteredWorkers);
            const comparisonClass = difference === null ? "" :
                ` booked-subcontractors__registered--${difference === 0 ? "matched" : "mismatch"}`;
            const registeredCount = <>{ renderCount(registered) }
                { difference !== null && difference !== 0 && <span className="booked-subcontractors__difference">
                    { ` (${difference > 0 ? "+" : "\u2212"}${Math.abs(difference)})` }
                </span> }
            </>;
            return <div className={ `booked-subcontractors__registered${comparisonClass}` }>
                { isBookedWorkersValid(registered) && registered > 0 ? <div className="view-box-field">
                    <Link to={ `${EventConst.basePath}/${encodeURIComponent(String(eventID))}/users` }>
                        <span className="count">{ registeredCount }</span>
                        <span className="btn-view">{ viewLabel }</span>
                    </Link>
                </div> : <span>{ registeredCount }</span> }
            </div>;
        } },
    ];
    if (canEdit) columns.push({
        key: "actions", width: 56, align: "right", render: (_, booking) => <BookingActionField
            booking={ booking } text={ text } disabled={ writeDisabled }
            onEdit={ () => open("edit", booking) } onDelete={ () => open("delete", booking) } />,
    });
    return (
        <section className="booked-subcontractors" aria-label={ text("Title") }>
            <div className="table-action-bar booked-subcontractors__toolbar">
                <p className="booked-subcontractors__description">
                    <strong>{ registeredScopeHint }</strong><br />
                    { text("Description") }
                </p>
                { canEdit && <Space wrap>
                    <Button className="btn-icon-text" type="primary" icon={ <PlusOutlined /> } disabled={ writeDisabled }
                        onClick={ () => open("create") }>{ text("Add") }</Button>
                </Space> }
            </div>
            { refreshWarning && <Alert type="warning" showIcon message={ text(
                refreshWarning.code === "DELETE_UNCONFIRMED" ? "DeleteUnconfirmed" : "SavedRefreshFailed"
            ) } action={ retryButton } /> }
            { !dialog && actionError && !refreshWarning && <Alert type="error" showIcon message={ errorText(actionError, text) }
                action={ needsReload && !error ? retryButton : undefined } /> }
            { error && !refreshWarning && <Alert type="error" showIcon message={ text("LoadFailed") }
                action={ retryButton } /> }
            <Table className="table-main" rowKey="id" columns={ columns } dataSource={ error ? [] : rows }
                loading={ loading } pagination={ false } scroll={ { x: canEdit ? 800 : 560 } }
                locale={ { emptyText: <div className="no-data-box">
                    <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } />
                    <span className="no-data-text">{ error ? text("Unavailable") : text("Empty") }</span>
                </div> } } />
            { dialog && <Modal visible centered destroyOnClose className="modal-standard modal-form booked-subcontractors-modal" width={ 480 }
                modalRender={ modal => React.cloneElement(modal, {
                    ref: node => node?.closest("[role='dialog']")?.setAttribute("aria-labelledby", modalTitleID),
                }) }
                footer={ null } maskClosable={ !saving } closable={ !saving } keyboard={ !saving } onCancel={ close }>
                <strong id={ modalTitleID } className="modal-title">
                    { text(dialog.kind === "delete" ? "DeleteTitle" : dialog.kind === "edit" ? "EditTitle" : "Add") }
                </strong>
                { (actionError || error) && <Alert type="error" showIcon message={ actionError ? errorText(actionError, text) : text("LoadFailed") }
                    action={ needsReload || error ? retryButton : undefined } /> }
                { dialog.kind === "delete" ? <>
                    <p>{ text("DeleteDescription") }</p>
                    <p><strong>{ dialog.booking.relSubcontractor?.title || text("Unavailable") }</strong></p>
                    <div className="form-btn-holder">
                        <Button className="ant-btn-secondary bg-light-gray" disabled={ saving } onClick={ close }>{ text("Cancel") }</Button>
                        <Button className="btn-right" danger type="primary" disabled={ writeDisabled } loading={ saving }
                            onClick={ () => submit() }>{ text("Delete") }</Button>
                    </div>
                </> : <BookingForm booking={ dialog.booking } rows={ rows } text={ text } saving={ saving }
                    registeredScopeHint={ registeredScopeHint }
                    disabled={ writeDisabled } onSave={ submit } onCancel={ close } /> }
            </Modal> }
        </section>
    );
};

const BookedSubcontractors = ({ eventID }) => {
    const state = useBookedSubcontractors({ eventID });
    const translations = useTranslation();
    const text = key => translations[`BOOKED_SUBCONTRACTORS.${key}`];
    const { data: rolesData, loading: rolesLoading, error: rolesError } = useQuery(GET_ROLES, {
        variables: { first: 999 },
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
        notifyOnNetworkStatusChange: true,
    });
    const roles = rolesData?.roles?.data;
    const roleLabels = ["monteure", "sub_teamleader"].map(slug => {
        const label = Array.isArray(roles) ? roles.find(role => role?.slug === slug)?.label : null;
        return typeof label === "string" ? label.trim() : "";
    });
    const registeredScopeHint = rolesLoading ? text("RegisteredScopeLoading") :
        rolesError || roleLabels.some(label => !label) ? text("RegisteredScopeUnavailable") :
            text("RegisteredScopeHint").replace(/\{ (monteure|sub_teamleader) \}/g,
                (_, slug) => roleLabels[slug === "monteure" ? 0 : 1]);
    return <BookingContent key={ state.contextKey } eventID={ eventID } state={ state } text={ text }
        registeredScopeHint={ registeredScopeHint }
        viewLabel={ translations["STAFF_MANAGEMENT.Action_View"] } />;
};

export default BookedSubcontractors;
